// src/components/ui/Card.jsx - Updated ServiceCard with inline icons
// src/components/ui/Card.jsx - Refactored ServiceCard to use Button component
import React from 'react';
import Button from './Button'; // Import the Button component

/**
 * Reusable Card component with consistent styling
 * 
 * @param {Object} props - The component props
 * @param {string} [props.variant='default'] - Card variant (default, elevated, outlined)
 * @param {boolean} [props.hoverable=false] - Whether the card should have hover effects
 * @param {React.ReactNode} props.children - Card content
 * @param {React.ReactNode} [props.header] - Card header content
 * @param {React.ReactNode} [props.footer] - Card footer content
 * @param {string} [props.className] - Additional CSS classes
 * @param {Function} [props.onClick] - Click handler
 */
const Card = ({
  variant = 'default',
  hoverable = false,
  children,
  header,
  footer,
  className = '',
  onClick,
  ...props
}) => {
  // Base styles
  const baseStyles = "bg-white rounded-lg overflow-hidden";
  
  // Variant styles
  const variantStyles = {
    default: "shadow",
    elevated: "shadow-lg",
    outlined: "border border-gray-200",
  };
  
  // Hover styles
  const hoverStyles = hoverable ? "transition-all duration-300 hover:shadow-lg hover:translate-y-[-5px]" : "";
  
  // Interactive styles
  const interactiveStyles = onClick ? "cursor-pointer" : "";
  
  // Combined styles
  const combinedStyles = `
    ${baseStyles}
    ${variantStyles[variant] || variantStyles.default}
    ${hoverStyles}
    ${interactiveStyles}
    ${className}
  `.trim();
  
  return (
    <div 
      className={combinedStyles} 
      onClick={onClick} 
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      {...props}
    >
      {header && (
        <div className="card-header">
          {header}
        </div>
      )}
      
      <div className="card-body">
        {children}
      </div>
      
      {footer && (
        <div className="card-footer">
          {footer}
        </div>
      )}
    </div>
  );
};

// Service Card component built on top of the base Card - Updated to use Button component
export const ServiceCard = ({
  icon,
  title,
  description,
  actionText = 'Learn more',
  actionHref = '#',
  ...props
}) => {
  // Using secondary color (burgundy) for header background
  const bgGradient = 'from-secondary to-secondary-dark';
  
  const headerContent = (
    <div className={`py-4 px-5 bg-gradient-to-r ${bgGradient} text-white`}>
      <div className="flex items-center">
        {/* Icon container with golden color */}
        <div className="text-accent text-2xl mr-3 flex-shrink-0">
          {icon}
        </div>
        {/* Title next to the icon */}
        <h3 className="text-lg font-semibold">{title}</h3>
      </div>
    </div>
  );
  
  return (
    <Card 
      variant="default" 
      hoverable={true} 
      header={headerContent}
      {...props}
    >
      <div className="p-5 flex flex-col h-full">
        <p className="text-gray-600 flex-grow">{description}</p>
        
        {/* REFACTORED: Using Button component instead of custom anchor tag */}
        <Button 
          href={actionHref} 
          variant="textSecondary"
          size="sm"
          className="mt-4 group"
        >
          {actionText}
          <svg 
            xmlns="http://www.w3.org/2000/svg" 
            className="ml-1 h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" 
            viewBox="0 0 20 20" 
            fill="currentColor"
          >
            <path 
              fillRule="evenodd" 
              d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z" 
              clipRule="evenodd" 
            />
          </svg>
        </Button>
      </div>
    </Card>
  );
};

// Other components remain unchanged
export const TeamCard = ({
  name,
  position,
  bio,
  imageSrc,
  color = 'primary',
  socialLinks,
  actionText = 'View Profile',
  actionHref = '#',
  ...props
}) => {
  // Color variants to match the ServiceCard component
  const colorVariants = {
    primary: 'from-primary to-primary-dark',
    secondary: 'from-secondary to-secondary-dark',
    accent: 'from-accent to-accent-dark',
  };
  
  // Text color variants
  const textColorVariants = {
    primary: 'text-primary',
    secondary: 'text-secondary',
    accent: 'text-accent',
  };
  
  // Default to primary if an invalid color is provided
  const bgGradient = colorVariants[color] || colorVariants.primary;
  const textColor = textColorVariants[color] || textColorVariants.primary;
  
  return (
    <Card 
      variant="default" 
      hoverable={true} 
      {...props}
    >
      <div className={`overflow-hidden h-64 bg-gradient-to-r ${bgGradient}`}>
        {imageSrc ? (
          <img 
            src={imageSrc} 
            alt={name} 
            className="w-full h-full object-cover transition duration-500 group-hover:scale-110"
            onError={(e) => {
              e.target.onerror = null;
              e.target.style.display = 'none';
              e.target.parentElement.innerHTML = `<span class="text-white text-8xl flex items-center justify-center h-full">${name.charAt(0)}</span>`;
            }}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <span className="text-white text-8xl">{name.charAt(0)}</span>
          </div>
        )}
      </div>
      
      <div className="p-6">
        <h3 className="text-xl font-serif font-bold text-gray-900 mb-1">{name}</h3>
        <p className={`${textColor} font-medium mb-3`}>{position}</p>
        <p className="text-gray-600 mb-4 line-clamp-3">{bio}</p>
        
        {socialLinks && (
          <div className="flex space-x-3 mb-4">
            {socialLinks}
          </div>
        )}
        
        {/* REFACTORED: Using Button component instead of custom anchor tag */}
        <Button 
          href={actionHref} 
          variant={color}
          size="md"
        >
          {actionText}
        </Button>
      </div>
    </Card>
  );
};

export const ValueCard = ({
  icon,
  title,
  description,
  color = 'primary',
  ...props
}) => {
  // Color variants for different types of value cards
  const bgColorVariants = {
    primary: 'bg-primary-50',
    secondary: 'bg-secondary-50',
    accent: 'bg-accent-50',
  };
  
  const iconColorVariants = {
    primary: 'text-primary',
    secondary: 'text-secondary',
    accent: 'text-accent',
  };
  
  const borderColorVariants = {
    primary: 'border-primary',
    secondary: 'border-secondary',
    accent: 'border-accent',
  };
  
  // Default to primary if an invalid color is provided
  const bgColor = bgColorVariants[color] || bgColorVariants.primary;
  const iconColor = iconColorVariants[color] || iconColorVariants.primary;
  const borderColor = borderColorVariants[color] || borderColorVariants.primary;
  
  return (
    <Card 
      variant="outlined" 
      className={`${bgColor} hover:bg-opacity-80 border-l-4 ${borderColor}`}
      hoverable={true} 
      {...props}
    >
      <div className="p-6">
        <div className={`${iconColor} mb-4`}>
          {icon}
        </div>
        <h3 className="text-xl font-serif font-bold mb-3 text-gray-900">{title}</h3>
        <p className="text-gray-700">{description}</p>
      </div>
    </Card>
  );
};

export default Card;